import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { Form, FormField, FormItem } from '~/atoms/shadcn/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { createKioskLearner } from '~/service/PublicKiosk/fetchApiClient'
import { AppAsyncSelectV2, AppButton, AppTextInput, useToastV2 } from '@easyllama/ui-components'

const formSchema = z.object({
  first_name: z.string().min(1, { message: 'First name is required' }),
  middle_name: z.string().optional(),
  last_name: z.string().min(1, { message: 'Last name is required' }),
  email: z
    .union([z.string().email({ message: 'Please enter a valid email address' }), z.literal('')])
    .optional(),
  phone: z.string().optional(),
  role: z.preprocess(
    val => (val === null || val === undefined ? undefined : val),
    z.number({ required_error: 'Role is required' }),
  ),
  region_id: z.number().nullable(),
  manager_id: z.number().nullable(),
  location_id: z.number().nullable(),
  employment_type: z.number().nullable(),
  industry_id: z.number().nullable(),
  department_id: z.number().nullable(),
  title: z.string().optional(),
  internal_id: z.string().optional(),
  trigger_automations: z.boolean().default(true),
  address_attributes: z
    .object({
      city: z.string().optional(),
      region: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
})

const NAME_ROW_FIELDS = [
  { name: 'first_name', label: 'First Name', required: true, className: 'w-full' },
  { name: 'middle_name', label: 'Middle Name', className: 'w-full' },
]

const LAST_NAME_EMAIL_FIELDS = [
  { name: 'last_name', label: 'Last Name', required: true },
  { name: 'email', label: 'Email', type: 'email' },
]

const ADDRESS_FIELDS = [
  { name: 'address_attributes.city', label: 'City' },
  { name: 'address_attributes.region', label: 'State / Province' },
  { name: 'address_attributes.country', label: 'Country' },
]

const ROLE_LOCATION_FIELDS = [
  {
    type: 'select',
    name: 'role',
    label: 'Role',
    metadataKey: 'roles',
    required: true,
    className: undefined,
  },
  {
    type: 'select',
    name: 'location_id',
    label: 'Location',
    metadataKey: 'locations',
    className: undefined,
    visibleWhen: metadata => metadata?.locations?.length > 0,
  },
]

const REGION_FIELD = {
  type: 'select',
  name: 'region_id',
  label: 'Training Region',
  metadataKey: 'regions',
  className: undefined,
  emptyMessage: 'No regions found',
  visibleWhen: metadata => metadata?.regions?.length > 0,
}

const ADDITIONAL_FIELDS = [
  { name: 'title', label: 'Title' },
  {
    type: 'select',
    name: 'manager_id',
    label: 'Manager',
    metadataKey: 'managers',
    visibleWhen: metadata => metadata?.managers?.length > 0,
  },
  {
    type: 'select',
    name: 'employment_type',
    label: 'Employment type',
    metadataKey: 'employment_types',
    emptyMessage: 'No employment types found',
    visibleWhen: metadata => metadata?.employment_types?.length > 0,
  },
  {
    type: 'select',
    name: 'industry_id',
    label: 'Industry',
    metadataKey: 'industries',
    emptyMessage: 'No industries found',
    visibleWhen: metadata => metadata?.industries?.length > 0,
  },
  { name: 'phone', label: 'Phone number', type: 'tel', className: 'py-2' },
  {
    type: 'select',
    name: 'department_id',
    label: 'Department',
    metadataKey: 'departments',
    visibleWhen: metadata => metadata?.departments?.length > 0,
  },
  { name: 'internal_id', label: 'Employee Id', className: 'py-2' },
]

const getDefaultRoleId = formMetadata =>
  formMetadata?.roles?.find(role => role.name?.toLowerCase().replace(/[^a-z]/g, '') === 'nonsupervisor')?.id ?? null

const AddNewLearnerKioskForm = ({ secretId, companyId, trainingId, formMetadata = {}, onClose }) => {
  const toast = useToastV2()
  const [showAdditional, setShowAdditional] = useState(false)

  const parseSelectId = value => (value?.value != null ? Number(value.value) : null)

  const getFieldError = name => name.split('.').reduce((errors, key) => errors?.[key], form.formState.errors)?.message

  const getAsyncSelectValue = (fieldValue, metadataKey) => {
    if (fieldValue === null) return null

    const item = formMetadata?.[metadataKey]?.find(item => item.id === Number(fieldValue))
    return item ? { label: item.name, value: String(fieldValue) } : null
  }

  const getAsyncSelectFetchOptions = metadataKey => () => ({
    options:
      formMetadata?.[metadataKey]?.map(item => ({
        label: item.name,
        value: String(item.id),
      })) || [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
    },
  })

  // Do not wrap EDS inputs in shadcn FormControl: it overwrites aria-describedby with
  // ids that FormMessage never renders, so field errors are not associated (CAT-1492).
  const renderTextField = ({ name, label, type = 'text', required = false, className }) => (
    <FormField
      key={name}
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <AppTextInput
            type={type}
            label={label}
            placeholder='Type Here'
            {...field}
            value={field.value ?? ''}
            required={required}
            error={getFieldError(name)}
          />
        </FormItem>
      )}
    />
  )

  const renderAsyncSelectField = ({ name, label, metadataKey, required = false, className, emptyMessage }) => (
    <FormField
      key={name}
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <AppAsyncSelectV2
            label={label}
            placeholder='Please select'
            value={getAsyncSelectValue(field.value, metadataKey)}
            onChange={value => field.onChange(parseSelectId(value))}
            required={required}
            fetchOptions={getAsyncSelectFetchOptions(metadataKey)}
            emptyMessage={emptyMessage || `No ${label.toLowerCase()}s found`}
            multiSelect={false}
            paginate={false}
            error={getFieldError(name)}
          />
        </FormItem>
      )}
    />
  )

  const renderField = field =>
    field.type === 'select' ? renderAsyncSelectField(field) : renderTextField(field)

  const isFieldVisible = field => (field.visibleWhen ? field.visibleWhen(formMetadata) : true)

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: getDefaultRoleId(formMetadata),
      region_id: null,
      manager_id: null,
      location_id: null,
      employment_type: null,
      industry_id: null,
      department_id: null,
      title: '',
      internal_id: '',
      trigger_automations: true,
      address_attributes: {
        city: '',
        region: '',
        country: 'US',
      },
    },
  })

  useEffect(() => {
    if (form.getValues('role') != null) return
    const defaultRoleId = getDefaultRoleId(formMetadata)
    if (defaultRoleId != null) form.setValue('role', defaultRoleId)
  }, [formMetadata, form])

  const onSubmit = async formData => {
    try {
      const payload = { ...formData }

        // Convert number fields - they're already numbers from AppAsyncSelectV2, but ensure null handling
        ;[
          'role',
          'employment_type',
          'industry_id',
          'location_id',
          'region_id',
          'manager_id',
          'department_id',
        ].forEach(field => {
          if (payload[field] !== null && payload[field] !== undefined) {
            payload[field] = Number(payload[field])
          } else {
            payload[field] = null
          }
        })

      // Only send address if a meaningful field was filled in; otherwise omit it
      // so we don't persist an empty address record. `country` defaults to 'US',
      // so it doesn't count toward "the user entered an address".
      const { country: _country, ...addressWithoutCountry } = payload.address_attributes ?? {}
      if (!Object.values(addressWithoutCountry).some(v => v?.trim?.())) {
        delete payload.address_attributes
      }

      const response = await createKioskLearner(secretId, {
        learner: payload,
        ...(trainingId ? { training_id: trainingId } : {}),
      })
      if (response?.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl
        return
      }
      toast.success('Learner created successfully', { showCloseButton: false })
      onClose?.()
    } catch (error) {
      toast.critical(error?.message || error?.errors?.[0] || 'Failed to create learner', { showCloseButton: false })
    }
  }

  return (
    <Form {...form}>
      <form className='flex flex-col gap-4' onSubmit={form.handleSubmit(onSubmit)}>
        <p className='text-xs text-llama-grey-5'>Asterisks (*) indicate required fields.</p>

        <div className='flex justify-between items-center gap-6'>
          {NAME_ROW_FIELDS?.map(renderTextField)}
        </div>

        {LAST_NAME_EMAIL_FIELDS?.map(renderTextField)}

        {ROLE_LOCATION_FIELDS.filter(isFieldVisible)?.map(field => (
          <React.Fragment key={field.name}>{renderField(field)}</React.Fragment>
        ))}

        {/* Address */}
        <div className='flex flex-col gap-4'>
          <span className='text-sm font-bold text-llama-grey-7'>Address</span>
          {ADDRESS_FIELDS?.map(renderTextField)}
        </div>

        {isFieldVisible(REGION_FIELD) && renderField(REGION_FIELD)}

        {/* Collapsible Additional Fields */}
        <div>
          <button
            type='button'
            className='flex items-center w-full text-sm font-bold text-llama-grey-7 cursor-pointer'
            onClick={() => setShowAdditional(v => !v)}
            aria-expanded={showAdditional}
            aria-controls='additional-fields-accordion'
          >
            Show additional fields
            {showAdditional ? (
              <ChevronUp className='w-4 h-4 ml-2' />
            ) : (
              <ChevronDown className='w-4 h-4 ml-2' />
            )}
          </button>
        </div>

        <div
          id='additional-fields-accordion'
          className={clsx('overflow-hidden transition-[max-height,opacity] duration-[400ms]', showAdditional ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0')}
          aria-hidden={!showAdditional}
        >
          {showAdditional && (
            <div className='flex flex-col gap-2'>
              {ADDITIONAL_FIELDS.filter(isFieldVisible)?.map(field => (
                <React.Fragment key={field.name}>{renderField(field)}</React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className='absolute bottom-4 right-6'>
          <AppButton type='submit' disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Adding...' : 'Add'}
          </AppButton>
        </div>
      </form>
    </Form>
  )
}

export default AddNewLearnerKioskForm
